/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'
import { createUseStyles } from 'react-jss'
import { useTranslation } from 'react-i18next'

import ActionButton from '../ActionButton'
import CloudinaryImage from '../CloudinaryImage'
import ArticleCard from '../ArticleCard'
import ArticleFragment from '../ArticleFragment'
import Sharing from '../Sharing'
import { iconsKeys } from '../Icon/Icon.assets'
import { colors, sizes } from '../../theme'
import { imageTypes } from '../CloudinaryImage/types'
import { getWindowSize } from '../../utils/WindowSize'
import withMemo from '../../decorators/WithMemo'
import { BlocksParser } from '../../helpers/BlocksHelpers'

import styles from './styles'


const defaultImage = 'defaults/article-banner'
const useStyles = createUseStyles(styles)

const ArticleDetail = (props) => {
  const classes = useStyles(props)
  const {
    className = '',
    title = null,
    description = null,
    blocks = null,
    image = null,
    credits = null,
    author = null,
    share = null,
    publishedAt = null,
    category = null,
    textProps = null,
    previousPost = null,
    nextPost = null,
    linkedArticles = [],
  } = props

  const [isDesktop, setIsDesktop] = useState(false)

  const { t } = useTranslation()

  function handleResize() {
    const windowSize = getWindowSize()

    if (!isDesktop && windowSize.width >= sizes.tablet) {
      setIsDesktop(true)
    } else if (isDesktop && windowSize.width < sizes.tablet) {
      setIsDesktop(false)
    }
  }

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  })

  function renderCoverImage() {
    return (
      <CloudinaryImage
        id={image || defaultImage}
        className={classes.image}
        type={imageTypes.Image}
        covered
        options={{
          width: isDesktop ? '940' : '767',
          height: isDesktop ? '410' : '431',
          crop: 'fill',
        }}
        lazyLoad
        alt={t('SEO_actu_cover_image') + title}
      >
        {credits && credits !== '' && (
          <span
            className={classes.creditsDefault}
            dangerouslySetInnerHTML={{ __html: `${textProps.creditsLabel} ${credits}` }}
          />
        )}
      </CloudinaryImage>
    )
  }

  function renderPortraitImage() {
    return (
      <CloudinaryImage
        id={image || defaultImage}
        className={classes.imagePortrait}
        type={imageTypes.Image}
        covered
        options={{
          width: isDesktop ? '220' : '320',
          height: isDesktop ? '380' : '180',
          crop: 'fill',
        }}
        lazyLoad
        alt={t('SEO_actu_portrait_image') + title}
      >
        {credits && credits !== '' && (
          <span
            className={classes.creditsPortrait}
            dangerouslySetInnerHTML={{ __html: `${textProps.creditsLabel} ${credits}` }}
          />
        )}
      </CloudinaryImage>
    )
  }

  function renderHeader() {
    return (
      <header>
        {image && (category.theme !== 'PORTRAIT' || (category.theme === 'PORTRAIT' && !isDesktop)) && renderCoverImage()}
      </header>
    )
  }

  function renderMain() {
    return (
      <div className={classes.main}>
        {title && (
          <h1
            className={classes.title}
            dangerouslySetInnerHTML={{ __html: title }}
          />
        )}
        {description && (
          <h2
            className={classes.description}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}

        <div className={classes.blockContainer}>
          <BlocksParser
            blocks={blocks}
            classes={{ container: classes.block }}
          />
        </div>
      </div>
    )
  }

  // eslint-disable-next-line complexity
  function renderSidebar() {
    let label = ''

    if (category.theme === 'PORTRAIT') {
      label += textProps.authorLabelPortrait
    } else {
      label += textProps.authorLabelDefault
    }

    if (author.firstName !== '') {
      label += ` <strong>${author.firstName}</strong>`
    }

    if (author.lastName !== '') {
      label += ` <strong>${author.lastName}</strong>`
    }

    if (author.company !== '') {
      label += ` - ${author.company}`
    }

    return (
      <div className={classes.sidebar}>
        {image && category.theme === 'PORTRAIT' && isDesktop && renderPortraitImage()}
        <div className={classes.creditsContainer}>
          <div className={classes.creditsContainerTop}>
            {label !== '' && (
              <div
                className={classes.author}
                dangerouslySetInnerHTML={{ __html: label }}
              />
            )}
            {publishedAt && (
              <div
                className={classes.date}
                dangerouslySetInnerHTML={{ __html: `${textProps.dateLabel} ${publishedAt}` }}
              />
            )}
          </div>
          <Sharing
            {...share}
            className={classes.sharing}
          />
        </div>
        {props?.documentPdf && textProps.downloadPdf && (
          <div className={classes.downloadPdfContainer}>
            <ActionButton
              className={classes.downloadPdf}
              label={textProps.downloadPdf}
              link={props?.documentPdf}
              color="secondary"
              isOutlined
              target="_blank"
              download={`${title}.pdf`}
            />
          </div>
        )}
      </div>
    )
  }

  // eslint-disable-next-line complexity
  function renderFooter() {
    return (
      <div className={classes.footer}>
        <div className={cx(classes.prevNextLink, classes.prevNextLinkLeft)}>
          <ActionButton
            link={previousPost.link}
            label={textProps.previousPostLabel}
            icon={iconsKeys.ArrowLeft}
            isOutlined
            isDisabled={!previousPost || !previousPost.link}
            color="secondary"
            colorIcon={!previousPost || !previousPost.link ? colors.silver : colors.darkBlue}
            colorIconHover={colors.pureWhite}
          />
          {previousPost && (
            <span
              className={classes.prevNextPostTitle}
              dangerouslySetInnerHTML={{ __html: previousPost.title }}
            />
          )}
        </div>
        <div className={classes.prevNextLink}>
          <ActionButton
            link={nextPost.link}
            label={textProps.nextPostLabel}
            icon={iconsKeys.ArrowRight}
            isOutlined
            isDisabled={!nextPost || !nextPost.link}
            color="secondary"
            colorIcon={!nextPost || !nextPost.link ? colors.silver : colors.darkBlue}
            colorIconHover={colors.pureWhite}
            iconRight
          />
          {nextPost && (
            <span
              className={classes.prevNextPostTitle2}
              dangerouslySetInnerHTML={{ __html: nextPost.title }}
            />
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={cx(classes.container, ((category.theme === 'PORTRAIT' && isDesktop) || !image) && classes.paddingTop, className)}>
      {renderHeader()}
      {category.name && (
        <div
          className={classes.typeLabel}
          dangerouslySetInnerHTML={{ __html: category.name }}
        />
      )}
      <div className={classes.layout}>
        {renderMain()}
        {renderSidebar()}
      </div>
      <footer>
        {renderFooter()}
      </footer>
      {linkedArticles && linkedArticles.length >= 1 && (
        <div className={classes.linkedPosts}>
          <p
            dangerouslySetInnerHTML={{ __html: textProps.linkedPostsTitle }}
            className={classes.linkedPostsTitle}
          />
          <ArticleFragment
            articles={linkedArticles}
            isLinkedArticles
          />
        </div>
      )}
    </div>
  )
}

ArticleDetail.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  blocks: PropTypes.arrayOf(PropTypes.shape({

  })),
  credits: PropTypes.string,
  author: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    company: PropTypes.string,
  }),
  share: PropTypes.shape(Sharing.propTypes),
  publishedAt: PropTypes.string,
  linkedArticles: PropTypes.arrayOf(PropTypes.shape(ArticleCard.propTypes)),
  textProps: PropTypes.shape(PropTypes.string),
  documentPdf: PropTypes.string,
  category: PropTypes.shape({
    name: PropTypes.string,
    theme: PropTypes.string,
    bgColor: PropTypes.string,
  }),
  previousPost: PropTypes.shape({
    title: PropTypes.string,
    link: PropTypes.string,
  }),
  nextPost: PropTypes.shape({
    title: PropTypes.string,
    link: PropTypes.string,
  }),
}


export default withMemo()(ArticleDetail)
